import React from 'react';
import { AlertTriangle, Banknote, Users, RefreshCw, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import LoadingSpinner from '../LoadingSpinner';
import { formatMontant, formatNumber } from './cantineUtils';

// Repas servis a un occasionnel sans paiement rattache : creances a recouvrer.
// N'apparait que si l'etablissement autorise le repas non paye.
const CantineCreancesSection = ({
  isLoading,
  data,
  autoriserRepasNonPaye,
  isSyncing,
  onSynchroniser,
  onRefresh
}) => {
  const resume = data?.resume || {};
  const lignes = data?.lignes || [];

  return (
    <div className="space-y-4">
      {/* Rappel de la règle en vigueur */}
      <div className={`rounded-lg border-2 p-4 ${autoriserRepasNonPaye ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
        <div className="flex items-start gap-3">
          {autoriserRepasNonPaye
            ? <AlertTriangle size={20} className="text-orange-600 mt-0.5 flex-shrink-0" />
            : <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />}
          <div>
            <h5 className={`font-semibold ${autoriserRepasNonPaye ? 'text-orange-900' : 'text-green-900'}`}>
              {autoriserRepasNonPaye
                ? 'Le repas non payé est autorisé'
                : 'Le repas non payé est bloqué'}
            </h5>
            <p className={`text-sm mt-1 ${autoriserRepasNonPaye ? 'text-orange-800' : 'text-green-800'}`}>
              {autoriserRepasNonPaye
                ? "Un occasionnel peut être servi sans avoir payé : chaque repas dans ce cas génère une créance listée ci-dessous, à recouvrer à la caisse."
                : "Un occasionnel ne peut pas être pointé présent sans paiement enregistré. Aucune créance ne peut donc se créer. Le paramètre se change dans l'onglet Paramètres."}
            </p>
          </div>
        </div>
      </div>

      {/* Cartes de synthèse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-red-50 border-2 border-red-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <Banknote size={22} className="text-red-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Montant à recouvrer</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-red-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Repas concernés</span>
                <span className="font-semibold text-gray-700">{formatNumber(resume.nombre)}</span>
              </div>
              <div className="border-t border-red-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Total dû</span>
                <span className="text-xl font-bold text-red-600">{formatMontant(resume.montant_total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-2 border-orange-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Users size={22} className="text-orange-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Élèves concernés</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">À relancer</span>
                <span className="font-semibold text-orange-600">Familles</span>
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Total</span>
                <span className="text-xl font-bold text-orange-600">{formatNumber(resume.eleves_concernes)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-2 border-blue-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <RefreshCw size={22} className="text-blue-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Inscriptions obsolètes</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
              <p className="text-xs text-gray-600">
                Suspend les inscriptions des élèves radiés, transférés ou archivés.
              </p>
              <Button
                size="sm"
                className="w-full bg-[#246990] hover:bg-[#1f5a7d] text-white"
                onClick={onSynchroniser}
                disabled={isSyncing}
              >
                <RefreshCw size={14} className={`mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des créances */}
      <Card>
        <CardHeader>
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <CardTitle className="text-xl text-red-600 flex items-center gap-2">
                <AlertTriangle size={24} />
                Repas non couverts
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                repas servis à un occasionnel sans paiement enregistré
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={onRefresh}>Actualiser</Button>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner message="Chargement des créances..." color="#c66d59" />
            </div>
          ) : lignes.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-green-200">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-gray-700 font-medium">Aucun repas non couvert</p>
              <p className="text-gray-500 text-sm mt-2">
                Tous les repas servis aux occasionnels ont bien un paiement rattaché.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-red-100 border-b border-red-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-red-900 uppercase">Date du repas</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-red-900 uppercase">Élève</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-red-900 uppercase">Classe</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-red-900 uppercase">Montant dû</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {lignes.map((ligne) => (
                    <tr key={ligne.pointage_id} className="hover:bg-red-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(`${ligne.date_service}T12:00:00`).toLocaleDateString('fr-FR')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{ligne.prenom} {ligne.nom}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{ligne.matricule || 'Sans matricule'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{ligne.classe_nom || '—'}</p>
                        <p className="text-xs text-gray-500">{ligne.section_nom || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm font-bold text-red-600">{formatMontant(ligne.montant_du)}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CantineCreancesSection;
